import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  Chip,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  Button,
  InputBase,
  useMediaQuery,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import SearchIcon from "@mui/icons-material/Search";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import useCachedQuery from "../hooks/useCachedQuery";
import useChainScroll from "../hooks/useChainScroll";
import { fetchPrograms, queryKeys, STALE_TIMES } from "../api/queries";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import { INTERVIEW_TRACKS, countTopics } from "../data/interviewPrepCurriculum";
import MentorshipCallout from "../components/MentorshipCallout";
import { formatProblemTitle } from "../common/utils";
import {
  findContinueInSection,
  PRACTICE_SECTION_ORDER,
} from "../common/practiceProgress";

const ACCENT = "#137274";
const CONTENT_MAX = 1320;

const DIFFICULTY_META = {
  easy: { label: "Easy", color: "#1a7f37" },
  medium: { label: "Medium", color: "#d97706" },
  hard: { label: "Hard", color: "#cf222e" },
};

const getAcceptanceRate = (problem) => {
  const ok = Number(problem?.successSubmission) || 0;
  const fail = Number(problem?.failedSubmission) || 0;
  const total = ok + fail;
  if (!total) return null;
  return Math.round((ok / total) * 100);
};

const SECTION_ORDER = PRACTICE_SECTION_ORDER;

const ProgressRing = ({ value, size = 36, stroke = 3.5, color = ACCENT }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, value)) / 100) * c;
  return (
    <Box
      sx={{ width: size, height: size, position: "relative", flexShrink: 0 }}
    >
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={alpha(color, 0.15)}
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
        />
      </svg>
      <Typography
        sx={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          fontSize: size < 34 ? "0.55rem" : "0.62rem",
          fontWeight: 800,
          color,
        }}
      >
        {value}%
      </Typography>
    </Box>
  );
};

const ProgramsPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const levelParam = searchParams.get('level');
  const [problems, setProblems] = useState({});
  const [loading, setLoading] = useState(true);
  const [level, setLevel] = useState("1");
  const [selectedSection, setSelectedSection] = useState(null);
  const [topicQuery, setTopicQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | unsolved | solved | bookmarked | has_notes
  const [difficultyFilter, setDifficultyFilter] = useState("all"); // all | easy | medium | hard
  const didInitRef = useRef(false);
  const mobileRailRef = useRef(null);
  const topicsScrollRef = useChainScroll();
  const problemsScrollRef = useChainScroll();

  const border = isDark ? "#30363d" : "rgba(15, 60, 70, 0.1)";
  const surface = isDark ? "#161b22" : "#ffffff";
  const muted = isDark ? "#8b949e" : "#5f6b73";
  const pageBg = isDark
    ? "#0f1419"
    : "linear-gradient(180deg, #eef6f8 0%, #f7f9fb 40%, #ffffff 100%)";

  const { data: programsData, loading: programsLoading } = useCachedQuery(
    queryKeys.programs,
    fetchPrograms,
    { staleTime: STALE_TIMES.programs },
  );

  useEffect(() => {
    if (!programsData) return;
    const problemData = Array.isArray(programsData) ? programsData : [];

    const structuredProblems = {};
    problemData.forEach((problem) => {
      const lvl = String(problem.level ?? "");
      const section = problem.section;
      if (!lvl || !section) return;

      if (!structuredProblems[lvl]) structuredProblems[lvl] = {};
      if (!structuredProblems[lvl][section])
        structuredProblems[lvl][section] = [];
      structuredProblems[lvl][section].push(problem);
    });

    Object.keys(structuredProblems).forEach((lvl) => {
      Object.keys(structuredProblems[lvl]).forEach((sect) => {
        structuredProblems[lvl][sect].sort(
          (a, b) =>
            (a.displayOrder || 0) - (b.displayOrder || 0) ||
            String(a._id || "").localeCompare(String(b._id || "")),
        );
      });
    });

    setProblems(structuredProblems);
    setLoading(false);
  }, [programsData]);

  useEffect(() => {
    if (programsLoading && !programsData) setLoading(true);
  }, [programsLoading, programsData]);

  useEffect(() => {
    if (loading) return;

    const availableLevels = Object.keys(problems);
    if (!availableLevels.length) return;

    const levelFromUrl = searchParams.get("level");
    const sectionFromUrl = searchParams.get("section");
    const levelFromStorage = localStorage.getItem("selectedLevel");
    const sectionFromStorage = localStorage.getItem("selectedSection");

    const requestedLevel = String(
      levelFromUrl || levelFromStorage || availableLevels[0],
    );
    const safeLevel = availableLevels.includes(requestedLevel)
      ? requestedLevel
      : availableLevels[0];
    setLevel(safeLevel);
    localStorage.setItem("selectedLevel", safeLevel);

    const sectionsForLevel = problems[safeLevel] || {};
    const ordered = SECTION_ORDER.filter((s) => sectionsForLevel[s]);

    if (levelFromUrl && !sectionFromUrl) {
      // Prefer first incomplete topic when switching level from URL
      const firstIncomplete =
        ordered.find((s) =>
          (sectionsForLevel[s] || []).some((p) => !p.isSolved),
        ) ||
        ordered[0] ||
        null;
      setSelectedSection(firstIncomplete);
      if (firstIncomplete)
        localStorage.setItem("selectedSection", firstIncomplete);
      return;
    }

    const requestedSection = sectionFromUrl || sectionFromStorage;
    if (requestedSection && sectionsForLevel[requestedSection]) {
      setSelectedSection(requestedSection);
      localStorage.setItem("selectedSection", requestedSection);
    } else {
      const firstIncomplete =
        ordered.find((s) =>
          (sectionsForLevel[s] || []).some((p) => !p.isSolved),
        ) ||
        ordered[0] ||
        null;
      setSelectedSection(firstIncomplete);
      if (firstIncomplete)
        localStorage.setItem("selectedSection", firstIncomplete);
    }
    didInitRef.current = true;
  }, [loading, problems, searchParams]);

  // Keep selected topic chip visible in mobile rail
  useEffect(() => {
    if (!isMobile || !selectedSection || !mobileRailRef.current) return;
    const chip = mobileRailRef.current.querySelector(
      `[data-topic="${CSS.escape(selectedSection)}"]`,
    );
    chip?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [selectedSection, isMobile]);

  const availableLevels = Object.keys(problems);
  const safeLevel = availableLevels.includes(level)
    ? level
    : availableLevels[0];

  const orderedSections = useMemo(
    () => SECTION_ORDER.filter((section) => problems[safeLevel]?.[section]),
    [problems, safeLevel],
  );

  const sectionStats = useMemo(() => {
    return orderedSections.map((section) => {
      const list = problems[safeLevel][section] || [];
      const solved = list.filter((p) => p.isSolved).length;
      const pct = list.length ? Math.round((solved / list.length) * 100) : 0;
      const done = list.length > 0 && solved === list.length;
      return { section, list, solved, pct, done, total: list.length };
    });
  }, [orderedSections, problems, safeLevel]);

  const filteredTopics = useMemo(() => {
    const q = topicQuery.trim().toLowerCase();
    if (!q) return sectionStats;
    return sectionStats.filter((s) => s.section.toLowerCase().includes(q));
  }, [sectionStats, topicQuery]);

  const levelProblems = Object.values(problems[safeLevel] || {}).flat();
  const totalProblems = levelProblems.length;
  const solvedProblems = levelProblems.filter((p) => p.isSolved).length;
  const progressPct =
    totalProblems > 0 ? Math.round((solvedProblems / totalProblems) * 100) : 0;

  const active =
    sectionStats.find((s) => s.section === selectedSection) ||
    filteredTopics[0] ||
    null;

  const continueTopic =
    sectionStats.find((s) => !s.done) || sectionStats[0] || null;

  const continueProblem = useMemo(
    () => (continueTopic ? findContinueInSection(continueTopic.list) : null),
    [continueTopic],
  );

  const activeContinueProblem = useMemo(
    () => (active ? findContinueInSection(active.list) : null),
    [active],
  );

  const filteredProblems = useMemo(() => {
    if (!active?.list?.length) return [];
    return active.list.filter((p) => {
      if (statusFilter === "solved" && !p.isSolved) return false;
      if (statusFilter === "unsolved" && p.isSolved) return false;
      if (statusFilter === "bookmarked" && !p.isBookmarked) return false;
      if (statusFilter === "has_notes" && !p.hasNote) return false;
      if (difficultyFilter !== "all") {
        if (String(p.difficulty || "").toLowerCase() !== difficultyFilter) {
          return false;
        }
      }
      return true;
    });
  }, [active, statusFilter, difficultyFilter]);

  const filtersActive = statusFilter !== "all" || difficultyFilter !== "all";

  // Global index offset for day numbering within selected section
  const sectionStartIndex = useMemo(() => {
    if (!active) return 0;
    let idx = 0;
    for (const s of orderedSections) {
      if (s === active.section) break;
      idx += (problems[safeLevel][s] || []).length;
    }
    return idx;
  }, [active, orderedSections, problems, safeLevel]);

  const totalDays = Math.ceil(totalProblems / 5) || 1;

  // Keeps the address bar (and, via the Helmet below, the tab title) in sync
  // with whatever level/section the user is actually looking at, so state is
  // shareable/bookmarkable and matches what page load already reads back
  // via `levelFromUrl`/`sectionFromUrl` above.
  const syncUrl = (lvl, section) => {
    const params = new URLSearchParams();
    params.set("level", lvl);
    if (section) params.set("section", section);
    navigate(`/practice-track?${params.toString()}`, { replace: true });
  };

  const selectSection = (section) => {
    setSelectedSection(section);
    localStorage.setItem("selectedSection", section);
    syncUrl(safeLevel, section);
  };

  const handleLevelChange = (_event, next) => {
    if (next == null) return;
    const value = String(next);
    setLevel(value);
    localStorage.setItem("selectedLevel", value);
    const sectionsForLevel = problems[value] || {};
    const ordered = SECTION_ORDER.filter((s) => sectionsForLevel[s]);
    const firstIncomplete =
      ordered.find((s) =>
        (sectionsForLevel[s] || []).some((p) => !p.isSolved),
      ) ||
      ordered[0] ||
      null;
    setSelectedSection(firstIncomplete);
    if (firstIncomplete)
      localStorage.setItem("selectedSection", firstIncomplete);
    setTopicQuery("");
    setStatusFilter("all");
    setDifficultyFilter("all");
    syncUrl(value, firstIncomplete);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 1.5,
          background: pageBg,
        }}
      >
        <CircularProgress size={28} sx={{ color: ACCENT }} />
        <Typography sx={{ color: muted, fontWeight: 600 }} variant="body2">
          Loading practice track…
        </Typography>
      </Box>
    );
  }

  if (!availableLevels.length) {
    return (
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 6, maxWidth: 900, mx: "auto" }}>
        <Typography sx={{ color: muted }}>No programs available.</Typography>
      </Box>
    );
  }

  const TopicRow = ({ item, compact = false }) => {
    const isActive = active?.section === item.section;
    return (
      <Box
        component="button"
        type="button"
        data-topic={item.section}
        onClick={() => selectSection(item.section)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.35,
          width: "100%",
          textAlign: "left",
          border: `1px solid ${isActive ? alpha(ACCENT, 0.45) : border}`,
          bgcolor: isActive ? alpha(ACCENT, isDark ? 0.18 : 0.08) : surface,
          borderRadius: 2.25,
          px: compact ? 1.25 : 1.4,
          py: compact ? 0.9 : 1,
          cursor: "pointer",
          transition: "border-color 0.15s, background 0.15s, transform 0.15s",
          font: "inherit",
          color: "inherit",
          "&:hover": {
            borderColor: alpha(ACCENT, 0.4),
            bgcolor: isDark ? alpha("#fff", 0.04) : alpha(ACCENT, 0.05),
          },
        }}
      >
        <ProgressRing
          value={item.pct}
          size={compact ? 32 : 36}
          color={item.done ? "#1a7f37" : ACCENT}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              fontWeight: isActive ? 800 : 700,
              fontSize: compact ? "0.8rem" : "0.86rem",
              letterSpacing: "-0.01em",
            }}
          >
            {item.section}
          </Typography>
          <Typography
            sx={{ color: muted, fontWeight: 600, fontSize: "0.72rem" }}
          >
            {item.solved}/{item.total}
            {item.done ? " · Done" : ""}
          </Typography>
        </Box>
        {item.done && (
          <CheckCircleIcon
            sx={{ fontSize: 18, color: "#1a7f37", flexShrink: 0 }}
          />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: "100vh", background: pageBg }}>
      <Helmet>
        <title>
          {levelParam
            ? `${selectedSection ? `${selectedSection} · ` : ''}DSA Practice Track — Level ${levelParam} | CrafterHack`
            : 'Practice Track | CrafterHack'}
        </title>
        <meta
          name="description"
          content={
            levelParam
              ? `${selectedSection ? `${selectedSection} — ` : ''}Level ${levelParam} of CrafterHack's structured DSA practice track — leveled sections and daily problem sets.`
              : "Structured coding practice path with leveled sections and daily problem sets."
          }
        />
        <link
          rel="canonical"
          href={
            levelParam
              ? `https://crafterhack.com/practice-track?level=${encodeURIComponent(levelParam)}`
              : 'https://crafterhack.com/practice-track'
          }
        />
      </Helmet>

      {/* Hero */}
      <Box
        sx={{
          px: { xs: 2, sm: 3, lg: 4 },
          pt: { xs: 2.5, md: 3.25 },
          pb: { xs: 2, md: 2.5 },
          borderBottom: `1px solid ${border}`,
          background: isDark
            ? `radial-gradient(800px 360px at 10% -30%, ${alpha(ACCENT, 0.3)}, transparent 55%), #0f1419`
            : `radial-gradient(800px 360px at 8% -40%, ${alpha(ACCENT, 0.16)}, transparent 55%), linear-gradient(180deg, #eef7f8 0%, #f8fafb 100%)`,
        }}
      >
        <Box sx={{ maxWidth: CONTENT_MAX, mx: "auto" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "flex-end" },
              justifyContent: "space-between",
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.5 }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(ACCENT, isDark ? 0.25 : 0.12),
                    color: ACCENT,
                  }}
                >
                  <RouteOutlinedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography
                  component="h1"
                  sx={{
                    fontWeight: 900,
                    letterSpacing: "-0.03em",
                    fontSize: { xs: "1.45rem", sm: "1.7rem" },
                    lineHeight: 1.15,
                  }}
                >
                  Practice Track
                </Typography>
              </Box>
              <Typography
                sx={{
                  color: muted,
                  maxWidth: 520,
                  fontSize: { xs: "0.85rem", sm: "0.9rem" },
                  lineHeight: 1.45,
                }}
              >
                Structured path by level and topic — pick a section and work
                through problems in order.
              </Typography>
            </Box>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={safeLevel}
              onChange={handleLevelChange}
              aria-label="Select level"
              sx={{
                bgcolor: surface,
                border: `1px solid ${border}`,
                borderRadius: 2,
                "& .MuiToggleButton-root": {
                  px: 2,
                  py: 0.75,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.8rem",
                  border: "none",
                  borderRadius: "8px !important",
                  m: 0.35,
                  "&.Mui-selected": {
                    bgcolor: `${ACCENT} !important`,
                    color: "#fff !important",
                  },
                },
              }}
            >
              {availableLevels.map((lvl) => (
                <ToggleButton key={lvl} value={lvl} aria-label={`Level ${lvl}`}>
                  Level {lvl}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
              gap: 2,
              alignItems: "center",
              p: { xs: 2, sm: 2.25 },
              borderRadius: 3,
              border: `1px solid ${border}`,
              bgcolor: surface,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 1.25,
                  mb: 1.15,
                  flexWrap: "wrap",
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: "0.88rem" }}>
                  Level {safeLevel} progress
                </Typography>
                <Typography
                  sx={{ color: muted, fontWeight: 700, fontSize: "0.78rem" }}
                >
                  {solvedProblems} / {totalProblems} solved · {progressPct}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progressPct}
                sx={{
                  height: 8,
                  borderRadius: 99,
                  bgcolor: alpha(ACCENT, 0.12),
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 99,
                    bgcolor: ACCENT,
                  },
                }}
              />
            </Box>

            {continueProblem && (
              <Button
                component={Link}
                to={`/problems/${continueProblem.slug}`}
                variant="contained"
                size="small"
                startIcon={<PlayArrowRoundedIcon sx={{ fontSize: "18px !important" }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  bgcolor: ACCENT,
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.7,
                  width: { xs: "100%", sm: 220 },
                  maxWidth: { xs: "100%", sm: 220 },
                  minWidth: 0,
                  justifyContent: "flex-start",
                  overflow: "hidden",
                  "& .MuiButton-startIcon": {
                    mr: 0.75,
                    flexShrink: 0,
                  },
                  "& .MuiButton-label, &": {
                    overflow: "hidden",
                  },
                  "&:hover": { bgcolor: "#0f5c5e" },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                    textAlign: "left",
                  }}
                >
                  Continue · {formatProblemTitle(continueProblem.title)}
                </Box>
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          maxWidth: CONTENT_MAX,
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 },
          py: { xs: 2, md: 2.5 },
        }}
      >
        {/* Mobile topic rail */}
        {isMobile && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1.25,
                p: 0.75,
                pl: 1.5,
                borderRadius: 2.5,
                border: `1px solid ${border}`,
                bgcolor: surface,
              }}
            >
              <SearchIcon sx={{ fontSize: 18, color: muted }} />
              <InputBase
                value={topicQuery}
                onChange={(e) => setTopicQuery(e.target.value)}
                placeholder="Filter topics…"
                sx={{ flex: 1, fontSize: "0.85rem" }}
              />
            </Box>
            <Box
              ref={mobileRailRef}
              sx={{
                display: "flex",
                gap: 1,
                overflowX: "auto",
                pb: 0.5,
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {filteredTopics.map((item) => {
                const isActive = active?.section === item.section;
                return (
                  <Chip
                    key={item.section}
                    data-topic={item.section}
                    clickable
                    onClick={() => selectSection(item.section)}
                    label={`${item.section} · ${item.solved}/${item.total}`}
                    sx={{
                      flexShrink: 0,
                      fontWeight: 700,
                      height: 32,
                      fontSize: "0.75rem",
                      bgcolor: isActive ? ACCENT : surface,
                      color: isActive ? "#fff" : "inherit",
                      border: `1px solid ${isActive ? ACCENT : border}`,
                      "& .MuiChip-label": { px: 1.4 },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2, md: 2 },
            alignItems: "stretch",
            height: { md: "calc(100dvh - 160px)" },
            minHeight: { md: 640 },
            maxHeight: { md: "calc(100dvh - 140px)" },
          }}
        >
          {/* Desktop topic sidebar */}
          {!isMobile && (
            <Box
              sx={{
                width: { md: 280, lg: 300 },
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                borderRadius: 3,
                border: `1px solid ${border}`,
                bgcolor: surface,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  borderBottom: `1px solid ${border}`,
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: "0.8rem", mb: 1 }}>
                  Topics · {orderedSections.length}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1,
                    py: 0.55,
                    borderRadius: 1.75,
                    border: `1px solid ${border}`,
                    bgcolor: isDark ? alpha("#fff", 0.03) : alpha(ACCENT, 0.04),
                  }}
                >
                  <SearchIcon sx={{ fontSize: 16, color: muted }} />
                  <InputBase
                    value={topicQuery}
                    onChange={(e) => setTopicQuery(e.target.value)}
                    placeholder="Filter topics…"
                    sx={{ flex: 1, fontSize: "0.8rem" }}
                  />
                </Box>
              </Box>

              <Box
                ref={topicsScrollRef}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overscrollBehavior: "auto",
                  p: 1.15,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                  scrollbarWidth: "thin",
                }}
              >
                {filteredTopics.length === 0 ? (
                  <Typography variant="body2" sx={{ color: muted, p: 1 }}>
                    No topics match that filter.
                  </Typography>
                ) : (
                  filteredTopics.map((item) => (
                    <TopicRow key={item.section} item={item} compact />
                  ))
                )}
              </Box>
            </Box>
          )}

          {/* Problem panel */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: { xs: 520, md: 0 },
              display: "flex",
              flexDirection: "column",
              borderRadius: 3,
              border: `1px solid ${border}`,
              bgcolor: surface,
              overflow: "hidden",
            }}
          >
            {active ? (
              <>
                <Box
                  sx={{
                    px: { xs: 1.75, sm: 2.25 },
                    py: 1.5,
                    borderBottom: `1px solid ${border}`,
                    flexShrink: 0,
                    background: isDark
                      ? `linear-gradient(135deg, ${alpha(ACCENT, 0.16)}, transparent)`
                      : `linear-gradient(135deg, ${alpha(ACCENT, 0.08)}, transparent)`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: { xs: "flex-start", sm: "center" },
                      justifyContent: "space-between",
                      gap: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          fontSize: { xs: "1.05rem", sm: "1.2rem" },
                          letterSpacing: "-0.02em",
                          mb: 0.3,
                        }}
                      >
                        {active.section}
                      </Typography>
                      <Typography sx={{ color: muted, fontSize: "0.82rem" }}>
                        {active.solved} of {active.total} solved
                        {filtersActive
                          ? ` · showing ${filteredProblems.length}`
                          : active.done
                            ? " — topic complete"
                            : " · five problems ≈ one day"}
                      </Typography>
                    </Box>
                    <ProgressRing
                      value={active.pct}
                      size={44}
                      stroke={4}
                      color={active.done ? "#1a7f37" : ACCENT}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={active.pct}
                    sx={{
                      mt: 1.35,
                      height: 6,
                      borderRadius: 99,
                      bgcolor: alpha(ACCENT, 0.1),
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 99,
                        bgcolor: active.done ? "#1a7f37" : ACCENT,
                      },
                    }}
                  />

                  <Box
                    sx={{
                      mt: 1.5,
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    {[
                      { id: "all", label: "All" },
                      { id: "unsolved", label: "Unsolved" },
                      { id: "solved", label: "Solved" },
                      { id: "bookmarked", label: "Bookmarked" },
                      { id: "has_notes", label: "Has notes" },
                    ].map((opt) => (
                      <Chip
                        key={opt.id}
                        size="small"
                        clickable
                        label={opt.label}
                        onClick={() => setStatusFilter(opt.id)}
                        sx={{
                          height: 26,
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          bgcolor:
                            statusFilter === opt.id
                              ? ACCENT
                              : isDark
                                ? alpha("#fff", 0.04)
                                : alpha(ACCENT, 0.05),
                          color: statusFilter === opt.id ? "#fff" : "inherit",
                          border: `1px solid ${
                            statusFilter === opt.id ? ACCENT : border
                          }`,
                        }}
                      />
                    ))}
                    <Box
                      sx={{
                        width: "1px",
                        height: 18,
                        bgcolor: border,
                        mx: 0.25,
                        display: { xs: "none", sm: "block" },
                      }}
                    />
                    {[
                      { id: "all", label: "Any" },
                      { id: "easy", label: "Easy", color: "#1a7f37" },
                      { id: "medium", label: "Med", color: "#d97706" },
                      { id: "hard", label: "Hard", color: "#cf222e" },
                    ].map((opt) => {
                      const selected = difficultyFilter === opt.id;
                      const chipColor = opt.color || ACCENT;
                      return (
                        <Chip
                          key={`diff-${opt.id}`}
                          size="small"
                          clickable
                          label={opt.label}
                          onClick={() => setDifficultyFilter(opt.id)}
                          sx={{
                            height: 26,
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            bgcolor: selected
                              ? opt.id === "all"
                                ? ACCENT
                                : alpha(chipColor, isDark ? 0.28 : 0.16)
                              : isDark
                                ? alpha("#fff", 0.04)
                                : alpha(ACCENT, 0.05),
                            color: selected
                              ? opt.id === "all"
                                ? "#fff"
                                : chipColor
                              : "inherit",
                            border: `1px solid ${
                              selected
                                ? opt.id === "all"
                                  ? ACCENT
                                  : alpha(chipColor, 0.5)
                                : border
                            }`,
                          }}
                        />
                      );
                    })}
                    {filtersActive && (
                      <Button
                        size="small"
                        onClick={() => {
                          setStatusFilter("all");
                          setDifficultyFilter("all");
                        }}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          color: muted,
                          minWidth: 0,
                          px: 0.75,
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                </Box>

                <Box
                  ref={problemsScrollRef}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overscrollBehavior: "auto",
                    scrollbarWidth: "thin",
                  }}
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`${safeLevel}-${active.section}-${statusFilter}-${difficultyFilter}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                    >
                      {filteredProblems.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: "center" }}>
                          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                            No problems match
                          </Typography>
                          <Typography
                            sx={{ color: muted, fontSize: "0.85rem", mb: 1.5 }}
                          >
                            Try another status or difficulty filter.
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => {
                              setStatusFilter("all");
                              setDifficultyFilter("all");
                            }}
                            sx={{
                              textTransform: "none",
                              fontWeight: 700,
                              color: ACCENT,
                            }}
                          >
                            Clear filters
                          </Button>
                        </Box>
                      ) : (
                        filteredProblems.map((problem) => {
                          const i = active.list.findIndex(
                            (p) =>
                              String(p._id) === String(problem._id) ||
                              p.slug === problem.slug,
                          );
                          const globalIndex = sectionStartIndex + Math.max(0, i);
                          const dayNumber = Math.floor(globalIndex / 5) + 1;
                          const displayDay =
                            dayNumber === totalDays
                              ? "Today"
                              : `Day ${dayNumber}`;
                          const difficulty = String(
                            problem.difficulty || "",
                          ).toLowerCase();
                          const meta = DIFFICULTY_META[difficulty];
                          const acceptance = getAcceptanceRate(problem);
                          const isContinueTarget =
                            activeContinueProblem?.slug === problem.slug;

                          return (
                            <Box
                              key={problem._id}
                              component={Link}
                              to={`/problems/${problem.slug}`}
                              onClick={() => {
                                localStorage.setItem(
                                  "selectedSection",
                                  active.section,
                                );
                              }}
                              sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: { xs: 1, sm: 1.5 },
                              px: { xs: 1.5, sm: 2.25 },
                              py: 1.2,
                              textDecoration: "none",
                              color: "inherit",
                              borderBottom: `1px solid ${border}`,
                              transition: "background 0.15s",
                              bgcolor: isContinueTarget
                                ? isDark
                                  ? alpha(ACCENT, 0.12)
                                  : alpha(ACCENT, 0.06)
                                : "transparent",
                              borderLeft: isContinueTarget
                                ? `3px solid ${ACCENT}`
                                : "3px solid transparent",
                              "&:last-of-type": { borderBottom: "none" },
                              "&:hover": {
                                bgcolor: isDark
                                  ? alpha("#fff", 0.04)
                                  : alpha(ACCENT, 0.05),
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: 1.25,
                                flexShrink: 0,
                                display: "grid",
                                placeItems: "center",
                                fontSize: "0.7rem",
                                fontWeight: 800,
                                color: muted,
                                bgcolor: isDark
                                  ? alpha("#fff", 0.04)
                                  : alpha(ACCENT, 0.06),
                                border: `1px solid ${border}`,
                              }}
                            >
                              {globalIndex + 1}
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontSize: { xs: "0.85rem", sm: "0.9rem" },
                                  fontWeight: 650,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  letterSpacing: "-0.01em",
                                }}
                              >
                                {formatProblemTitle(problem.title)}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: muted,
                                  display: { xs: "block", sm: "none" },
                                  fontSize: "0.7rem",
                                }}
                              >
                                {displayDay}
                                {acceptance != null
                                  ? ` · ${acceptance}% accepted`
                                  : ""}
                              </Typography>
                            </Box>

                            {meta && (
                              <Chip
                                size="small"
                                label={meta.label}
                                sx={{
                                  height: 22,
                                  fontSize: "0.65rem",
                                  fontWeight: 700,
                                  display: { xs: "none", sm: "inline-flex" },
                                  flexShrink: 0,
                                  bgcolor: alpha(
                                    meta.color,
                                    isDark ? 0.2 : 0.1,
                                  ),
                                  color: meta.color,
                                }}
                              />
                            )}

                            <Typography
                              sx={{
                                display: { xs: "none", sm: "block" },
                                minWidth: 44,
                                textAlign: "right",
                                flexShrink: 0,
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                fontVariantNumeric: "tabular-nums",
                                color:
                                  acceptance == null
                                    ? muted
                                    : acceptance >= 50
                                      ? "#1a7f37"
                                      : acceptance >= 30
                                        ? "#d97706"
                                        : "#cf222e",
                              }}
                              title={
                                acceptance == null
                                  ? "No submissions yet"
                                  : `${acceptance}% acceptance rate`
                              }
                            >
                              {acceptance == null ? "—" : `${acceptance}%`}
                            </Typography>

                            {isContinueTarget && (
                              <Chip
                                size="small"
                                label="Continue"
                                sx={{
                                  height: 22,
                                  fontSize: "0.62rem",
                                  fontWeight: 800,
                                  flexShrink: 0,
                                  bgcolor: alpha(ACCENT, isDark ? 0.25 : 0.15),
                                  color: ACCENT,
                                }}
                              />
                            )}

                            <Typography
                              sx={{
                                color: muted,
                                display: { xs: "none", sm: "block" },
                                minWidth: 52,
                                textAlign: "right",
                                flexShrink: 0,
                                fontWeight: 600,
                                fontSize: "0.72rem",
                              }}
                            >
                              {displayDay}
                            </Typography>

                            {problem.isSolved ? (
                              <CheckCircleIcon
                                sx={{
                                  fontSize: 20,
                                  color: "#1a7f37",
                                  flexShrink: 0,
                                }}
                              />
                            ) : (
                              <RadioButtonUncheckedIcon
                                sx={{
                                  fontSize: 20,
                                  color: "action.disabled",
                                  flexShrink: 0,
                                }}
                              />
                            )}
                          </Box>
                        );
                      })
                      )}
                    </motion.div>
                  </AnimatePresence>
                </Box>

                {/* Topic prev / next */}
                {orderedSections.length > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                      px: 1.75,
                      py: 1.25,
                      borderTop: `1px solid ${border}`,
                      flexShrink: 0,
                    }}
                  >
                    <Button
                      size="small"
                      disabled={orderedSections.indexOf(active.section) <= 0}
                      onClick={() => {
                        const i = orderedSections.indexOf(active.section);
                        if (i > 0) selectSection(orderedSections[i - 1]);
                      }}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        color: muted,
                      }}
                    >
                      ← Previous topic
                    </Button>
                    <Button
                      size="small"
                      disabled={
                        orderedSections.indexOf(active.section) >=
                        orderedSections.length - 1
                      }
                      onClick={() => {
                        const i = orderedSections.indexOf(active.section);
                        if (i < orderedSections.length - 1) {
                          selectSection(orderedSections[i + 1]);
                        }
                      }}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        color: ACCENT,
                      }}
                    >
                      Next topic →
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                  No topic selected
                </Typography>
                <Typography variant="body2" sx={{ color: muted }}>
                  Choose a topic from the list to see its problems.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Mentorship + Interview Prep */}
      <Box
        sx={{
          maxWidth: CONTENT_MAX,
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 },
          pt: { xs: 2.5, md: 3 },
          pb: { xs: 4, md: 5 },
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <MentorshipCallout variant="banner" />

        <Box
          sx={{
            borderRadius: 3,
            border: `1px solid ${border}`,
            bgcolor: surface,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 2.5 },
              py: 2,
              borderBottom: `1px solid ${border}`,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: 1.5,
              background: isDark
                ? `linear-gradient(135deg, ${alpha(ACCENT, 0.18)} 0%, transparent 60%)`
                : `linear-gradient(135deg, ${alpha(ACCENT, 0.08)} 0%, transparent 55%)`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(ACCENT, isDark ? 0.25 : 0.12),
                  color: ACCENT,
                  flexShrink: 0,
                }}
              >
                <MenuBookOutlinedIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 900,
                    letterSpacing: "-0.02em",
                    fontSize: "1.05rem",
                    mb: 0.25,
                  }}
                >
                  Interview Prep
                </Typography>
                <Typography
                  sx={{ color: muted, fontSize: "0.85rem", maxWidth: 420 }}
                >
                  Beyond DSA — System Design, LLD, CS fundamentals, and
                  behavioral.
                </Typography>
              </Box>
            </Box>
            <Button
              component={Link}
              to="/interview-prep"
              endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                color: ACCENT,
                px: 1.5,
                "&:hover": { bgcolor: alpha(ACCENT, 0.08) },
              }}
            >
              Browse all
            </Button>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "repeat(4, 1fr)",
              },
              gap: 1.5,
              p: { xs: 1.5, sm: 2 },
            }}
          >
            {INTERVIEW_TRACKS.map((track) => {
              const topics = countTopics(track);
              const trackAccent = track.accent || ACCENT;
              return (
                <Box
                  key={track.id}
                  component={Link}
                  to={`/interview-prep/${track.id}`}
                  sx={{
                    display: "block",
                    textDecoration: "none",
                    color: "inherit",
                    p: 1.75,
                    borderRadius: 2,
                    border: `1px solid ${border}`,
                    transition: "border-color 0.15s, background 0.15s",
                    "&:hover": {
                      borderColor: alpha(trackAccent, 0.45),
                      bgcolor: isDark
                        ? alpha("#fff", 0.03)
                        : alpha(trackAccent, 0.05),
                    },
                  }}
                >
                  <Chip
                    label={track.shortTitle}
                    size="small"
                    sx={{
                      mb: 1,
                      height: 22,
                      fontWeight: 800,
                      fontSize: "0.65rem",
                      bgcolor: alpha(trackAccent, isDark ? 0.22 : 0.12),
                      color: trackAccent,
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "0.92rem",
                      letterSpacing: "-0.01em",
                      mb: 0.5,
                    }}
                  >
                    {track.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: muted,
                      fontSize: "0.78rem",
                      lineHeight: 1.45,
                      mb: 1.25,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {track.homeBlurb}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: trackAccent,
                    }}
                  >
                    {topics} topics →
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProgramsPage;
